import { type DefaultRootState } from 'react-redux';
import {
  createSelector,
  createSlice,
  isAnyOf,
  type PayloadAction,
} from '@reduxjs/toolkit';
import {
  createApi,
  fetchBaseQuery,
  type BaseQueryApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { omit } from 'lodash';
import {
  SignInType,
  type GetSignInUrlArgs,
  type SignInArgs,
  type SignInTokens,
} from '@bbkAdminRedux/rtkq/auth-types';
import {
  preauthCompanySlice,
  type BbkClientConfig,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import { BrightbackAuth } from '@bbkAdminUtils/brightback-auth';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { getNowInSeconds } from '@bbkAdminUtils/utils';

// Re-export for backward compatibility
export {
  type GetSignInUrlArgs,
  type SignInArgs,
  type SignInTokens,
  SignInType,
} from '@bbkAdminRedux/rtkq/auth-types';
export { isSignedIn } from '@bbkAdminRedux/rtkq/auth-utils';

export const UNSUPPORTED_AUTH_SESSION_ERROR =
  'Stored auth session type is unsupported';

export const CONFIG_NOT_LOADED_ERROR = 'Config is not loaded';

const getClientConfig = (api: BaseQueryApi): BbkClientConfig => {
  const state = api.getState() as DefaultRootState;
  const { data: config } =
    preauthCompanySlice.endpoints.getClientConfig.select()(state);

  if (!config) {
    throw new Error(CONFIG_NOT_LOADED_ERROR);
  }

  return config;
};

const createAuth = (api: BaseQueryApi): BrightbackAuth => {
  return new BrightbackAuth(getClientConfig(api));
};

/**
 * Resolves the auth flow for a stored access-token type. Only `dev` and `cbidp`
 * (Chargebee IDP) remain supported. Legacy Cognito-backed sessions (`google`
 * and username sessions, which were persisted as `null`) resolve to
 * `undefined` so callers treat them as stale and force a fresh sign-in.
 */
export const resolveSigninTypeFromTokenType = (
  tokenType: 'cbidp' | 'dev' | 'google' | null | undefined
): SignInType | undefined => {
  switch (tokenType) {
    case 'dev':
      return SignInType.DEV;
    case 'cbidp':
      return SignInType.CHARGEBEE;
    default:
      return undefined;
  }
};

export const brightbackAuthSlice = createApi({
  reducerPath: 'brightbackAuth-rktq',
  baseQuery: fetchBaseQuery(),
  tagTypes: [],
  endpoints: (build) => ({
    openSignIn: build.mutation<void, GetSignInUrlArgs>({
      queryFn: async (args, api) => {
        try {
          const auth = createAuth(api);
          const url = await auth.getSignInUrl(args);
          // FIXME: side effect
          window.location.assign(url);
          // next line doesn't do anything, because window.location.assign redirects to different url
          // but to comply with rtkq contract we need to return it
          return { data: undefined };
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          };
        }
      },
    }),

    signIn: build.mutation<SignInTokens, SignInArgs>({
      queryFn: async (args, api) => {
        try {
          const auth = createAuth(api);
          const tokens = await auth.signIn(args);
          return { data: tokens };
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          };
        }
      },
    }),

    signOut: build.mutation<void, { redirect: string }>({
      queryFn: async (args, api) => {
        try {
          const refreshToken = BrightbackSession.getRefreshToken();
          const tokenType = BrightbackSession.getAccessTokenType();
          const config = getClientConfig(api);
          const type = resolveSigninTypeFromTokenType(tokenType);

          if (!type) {
            BrightbackSession.setAccessToken(null, 0);
            BrightbackSession.setRefreshToken(null);
            BrightbackSession.setAccessTokenType(null);
            window.location.assign(args.redirect);
            return { data: undefined };
          }

          const auth = new BrightbackAuth(config);

          await auth.signOut({
            refresh_token: refreshToken,
            type,
            redirect: args.redirect,
          });
          return { data: undefined };
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          };
        }
      },
    }),
    doRefreshTokens: build.mutation<SignInTokens, void>({
      queryFn: async (args, api) => {
        try {
          const refreshToken = BrightbackSession.getRefreshToken();
          const accessTokenType = BrightbackSession.getAccessTokenType();
          if (!refreshToken) throw new Error('User is not signed in');
          const config = getClientConfig(api);
          const type = resolveSigninTypeFromTokenType(accessTokenType);
          if (!type) throw new Error(UNSUPPORTED_AUTH_SESSION_ERROR);
          const auth = new BrightbackAuth(config);

          const tokens = await auth.refreshTokens({
            refresh_token: refreshToken,
            type,
          });
          return { data: tokens };
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          };
        }
      },
    }),
  }),
});

type AuthState = {
  auth?: {
    type: SignInType;
    // access_token: string;
    // refresh_token: string;
    exp: number; // expires at (timestamp in seconds)
  };
};

export const authPersistedSlice = createSlice({
  name: 'authPersisted-rtk',
  initialState: {} as AuthState,
  reducers: {
    saveTokens: (
      draftState,
      { payload: tokens }: PayloadAction<SignInTokens>
    ) => {
      draftState.auth = omit(tokens, ['refresh_token', 'access_token']);
      const expiresIn = tokens.exp - getNowInSeconds();
      const accessTokenType =
        tokens.type === SignInType.DEV
          ? 'dev'
          : tokens.type === SignInType.CHARGEBEE
            ? 'cbidp'
            : null;
      BrightbackSession.setAccessToken(tokens.access_token, expiresIn);
      BrightbackSession.setRefreshToken(tokens.refresh_token);
      BrightbackSession.setAccessTokenType(accessTokenType);
    },
    clearTokens: (draftState) => {
      draftState.auth = undefined;
      BrightbackSession.setAccessToken(null, 0);
      BrightbackSession.setAccessTokenType(null);
      BrightbackSession.setRefreshToken(null);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        brightbackAuthSlice.endpoints.signIn.matchFulfilled,
        brightbackAuthSlice.endpoints.doRefreshTokens.matchFulfilled
      ),
      authPersistedSlice.caseReducers.saveTokens
    );
    builder.addMatcher(
      brightbackAuthSlice.endpoints.signOut.matchFulfilled,
      authPersistedSlice.caseReducers.clearTokens
    );
  },
});

export const selectAuth = (state: DefaultRootState) =>
  state[authPersistedSlice.name].auth;

export const selectAuthExpiresIn = createSelector(selectAuth, (subState) => {
  const exp = subState?.exp;
  let expiresIn = 0;
  if (exp) {
    expiresIn = exp - getNowInSeconds();
  }
  return expiresIn > 5 ? expiresIn - 5 : 0;
});
